import { EllipsisIcon } from "@xola/icons";
import { Popover } from "@xola/ui-kit";
import { isExperiencePurchaseItem } from "common";
import React from "react";
import { ExperiencePurchaseItem, Guest, MerchandisePurchaseItem, Permission, Purchase } from "types";
import { IsGranted } from "../Common/IsGranted";
import { Menu } from "../Common/Menu";
import { SecondaryButton } from "../Common/SecondaryButton";
import { CommonActionModals } from "./PurchaseActionMenu/CommonActionModals";
import { CustomerInfoHeader } from "./PurchaseActionMenu/CustomerInfoHeader";
import { ExperienceMenuItems } from "./PurchaseActionMenu/ExperienceMenuItems";
import { ItemActionModals } from "./PurchaseActionMenu/ItemActionModals";
import { MerchandiseMenuItems } from "./PurchaseActionMenu/MerchandiseMenuItems";
import { usePurchaseMenuState } from "./PurchaseActionMenu/usePurchaseMenuState";

interface PurchaseActionMenuProps {
    size?: string;
    purchase: Purchase;
    item: ExperiencePurchaseItem | MerchandisePurchaseItem;
    isOrganizer?: boolean;
    popoverParentId?: string;
    onOpenChange?: (isOpen: boolean) => void;
    guest?: Guest;
    isVisible?: boolean;
    onClose?: () => void;
    isMerchandise?: boolean; // To show only merchandise-specific menu items
}

export const PurchaseActionMenu = ({
    size = "small",
    purchase: initialPurchase,
    item,
    isOrganizer = true,
    popoverParentId,
    onOpenChange,
    guest,
    isVisible: isVisibleGlobally = false,
    onClose,
    isMerchandise = false,
}: PurchaseActionMenuProps) => {
    const purchaseMenuState = usePurchaseMenuState({
        initialPurchase,
        item,
        isVisibleGlobally,
        onOpenChange,
        onClose,
    });
    const { menuState, menuActions } = purchaseMenuState;

    const commonModalState = {
        purchase: menuState.purchase,
        selectedOption: menuState.selectedOption,
        warningModalOpen: menuState.warningModalOpen,
        remainingAction: menuState.remainingAction,
        shouldRefetchPurchase: menuState.shouldRefetchPurchase,
        isLoading: menuState.isLoading,
        shouldPrintStart: menuState.shouldPrintStart,
        PostCheckInWarningModal: menuState.PostCheckInWarningModal,
    };

    const commonModalActions = {
        onModalClose: menuActions.handleModalClose,
        refreshPurchaseRow: menuActions.refreshPurchaseRow,
        setSelectedOption: menuActions.setSelectedOption,
        setWarningModalOpen: menuActions.setWarningModalOpen,
        setShouldPrintStart: menuActions.setShouldPrintStart,
    };

    const itemModalState = {
        purchase: menuState.purchase,
        event: menuState.event,
        selectedOption: menuState.selectedOption,
        selectedRefundOption: menuState.selectedRefundOption,
        refundAmount: menuState.refundAmount,
        returnPaymentSource: menuState.returnPaymentSource,
        modifiedPurchase: menuState.modifiedPurchase,
        shouldShowUnassignGuide: menuState.shouldShowUnassignGuide ?? false,
        cancelReservationModalData: menuState.cancelReservationModalData,
    };

    const itemModalActions = {
        onModalClose: menuActions.handleModalClose,
        onCloseAfterPurchaseChange: menuActions.handleCloseAfterPurchaseChange,
        onChangeArrivalContinue: menuActions.handleChangeArrivalContinue,
        onPurchaseInvalidate: menuActions.handlePurchaseInvalidate,
        onFlushPurchaseCache: menuActions.handleFlushPurchaseCache,
        onResetRefundOption: menuActions.resetSelectedRefundOption,
        onCancelReservationClose: menuActions.handleCancelReservationClose,
        setSelectedOption: menuActions.setSelectedOption,
        setSelectedRefundOption: menuActions.setSelectedRefundOption,
        setRefundAmount: menuActions.setRefundAmount,
        setReturnPaymentSource: menuActions.setReturnPaymentSource,
        setModifiedPurchase: menuActions.setModifiedPurchase,
        setCancelReservationModalData: menuActions.setCancelReservationModalData,
    };

    const showEllipsisButton = !!onOpenChange;

    return (
        <IsGranted permissions={Permission.PURCHASES_VIEW}>
            <Popover
                visible={menuState.isVisible}
                maxWidth={300}
                offset={[0, 10]}
                appendTo={
                    popoverParentId ? document.querySelector(`#${popoverParentId}`) ?? document.body : document.body
                }
                placement="right"
                onClickOutside={menuActions.handleClickOutside}
            >
                {showEllipsisButton && (
                    <SecondaryButton
                        data-testid="search-result-item-menu"
                        size={size}
                        onMouseDown={() => menuActions.setIsVisible(!menuState.isVisible)}
                    >
                        <EllipsisIcon />
                    </SecondaryButton>
                )}

                <Popover.Content>
                    <Menu>
                        {isOrganizer && <CustomerInfoHeader purchase={menuState.purchase} />}

                        {isMerchandise ? (
                            <MerchandiseMenuItems
                                purchase={menuState.purchase}
                                item={item as MerchandisePurchaseItem}
                                isOrganizer={isOrganizer}
                                onButtonClick={async (action) => menuActions.handleButtonClick(action, isMerchandise)}
                            />
                        ) : (
                            isExperiencePurchaseItem(item) && (
                                <ExperienceMenuItems
                                    purchase={menuState.purchase}
                                    item={item}
                                    isOrganizer={isOrganizer}
                                    guest={guest}
                                    canSendEmail={menuState.canSendEmail ?? false}
                                    shouldSendSms={menuState.shouldSendSms}
                                    isSendingConfirmationEmail={menuState.isSendingConfirmationEmail}
                                    isSendingConfirmationSms={menuState.isSendingConfirmationSms}
                                    isConfirmationEmailSent={menuState.isConfirmationEmailSent ?? false}
                                    isConfirmationSmsSent={menuState.isConfirmationSmsSent}
                                    onButtonClick={async (action) =>
                                        menuActions.handleButtonClick(action, isMerchandise)
                                    }
                                    onConfirmationMessage={menuActions.handleConfirmationMessage}
                                />
                            )
                        )}
                    </Menu>
                </Popover.Content>
            </Popover>

            <CommonActionModals menuState={commonModalState} menuActions={commonModalActions} onClose={onClose} />

            <ItemActionModals
                menuState={itemModalState}
                menuActions={itemModalActions}
                item={item}
                guest={guest}
                onClose={onClose}
            />
        </IsGranted>
    );
};
